import type { FileRoutesByPath } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import type {
  AppRoute,
  WidgetConfigEntry,
  WidgetPropsType,
} from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { EmptyWidget } from '~/components/widget/base/EmptyWidget';
import { useAppParams } from '~/helper/useAppParams';

export function makeQueryLoaderWidgetsRoute(
  route: AppRoute,
  dynamicConfigWidgets: (widgetProps: WidgetPropsType) => WidgetConfigEntry[],
  isDemo = false,
) {
  const Route = createFileRoute(route as keyof FileRoutesByPath)({
    component: function Body() {
      const params = useAppParams(Route);

      const query = useLoaderQuery(route, {
        isDemo,
        params: params,
      });

      if (query.isLoading) {
        return <LoadingWidget />;
      }

      if (query.isError) {
        return (
          <ErrorWidget
            error={query.error}
            onRetry={() => query.refetch()}
            isRetrying={query.isFetching && !query.isLoading}
          />
        );
      }

      if (!query.data) {
        return <EmptyWidget />;
      }

      const data = query.data as { readonly widgetProps?: WidgetPropsType };
      const widgetProps = data.widgetProps;

      if (
        !widgetProps ||
        typeof widgetProps !== 'object' ||
        Object.keys(widgetProps).length === 0
      ) {
        return <EmptyWidget />;
      }

      return <RenderWidgets widgets={dynamicConfigWidgets(widgetProps)} />;
    },
  });

  return Route;
}
